<template>
  <AuthLayout :max-width="448">
    <div class="cv-eyebrow mb-1">Adminisztráció</div>
    <h1 class="text-h6 mb-6">Bejelentkezés</h1>

    <v-form @submit.prevent="onLogin()">
      <v-alert v-if="sessionExpired" type="info" variant="tonal" class="mb-4">
        A munkameneted lejárt, jelentkezz be újra.
      </v-alert>

      <v-alert v-if="errorMessage" type="error" variant="tonal" class="mb-4">
        {{ errorMessage }}
      </v-alert>

      <v-text-field
        v-model="email"
        label="Email cím"
        prepend-inner-icon="mdi-email-outline"
        autofocus
      ></v-text-field>

      <v-text-field
        v-model="password"
        label="Jelszó"
        :append-inner-icon="visible ? 'mdi-eye-off' : 'mdi-eye'"
        :type="visible ? 'text' : 'password'"
        prepend-inner-icon="mdi-lock-outline"
        @click:append-inner="toggleVisibility"
      ></v-text-field>

      <div class="d-flex justify-end mb-6">
        <router-link class="cv-link text-caption" to="/forgot-password?admin">
          Elfelejtett jelszó?
        </router-link>
      </div>

      <v-btn
        color="primary"
        size="large"
        block
        type="submit"
        :loading="loading"
        :disabled="loading"
      >
        Bejelentkezés
      </v-btn>
    </v-form>
  </AuthLayout>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { resolveLoginRedirect } from "@/utils/loginRedirect";
import { classifyApiError } from "@/services/useApiError";
import { useAuthService } from "@/services/authService";
import AuthLayout from "@/components/layout/AuthLayout.vue";

/** The login endpoint answers 403 for an address that was never confirmed. */
const loginErrorMessage = (error: unknown): string => {
  switch (classifyApiError(error).kind) {
    case "unauthenticated":
      return "Érvénytelen email cím vagy jelszó";
    case "forbidden":
      return "Az e-mail címed még nincs megerősítve. Nézd meg a postaládádat.";
    case "unavailable":
      return "A szolgáltatás átmenetileg nem elérhető. Próbáld újra egy perc múlva.";
    default:
      return "Bejelentkezés sikertelen. Kérjük, próbálja újra.";
  }
};

export default defineComponent({
  components: { AuthLayout },
  setup() {
    const visible = ref(false);
    const email = ref("");
    const password = ref("");
    const router = useRouter();
    const route = useRoute();
    const loading = ref(false);
    const errorMessage = ref("");
    // Set by the axios interceptor, which only redirects here mid-session — so unlike the
    // router guard, it knows the user really did have a session and lost it.
    const sessionExpired = computed(() => route.query.expired === "1");
    const { adminLogin, isLoggedIn } = useAuthService();

    const toggleVisibility = () => {
      visible.value = !visible.value;
    };

    const onLogin = async () => {
      errorMessage.value = "";
      loading.value = true;
      try {
        await adminLogin(email.value, password.value);
        console.log("login succeded");
        router.push(resolveLoginRedirect(route.query, "admin"));
      } catch (error: unknown) {
        errorMessage.value = loginErrorMessage(error);
        password.value = "";
      } finally {
        loading.value = false;
      }
    };

    return {
      visible,
      toggleVisibility,
      onLogin,
      email,
      password,
      loading,
      errorMessage,
      sessionExpired,
    };
  },
});
</script>

<style scoped>
.cv-link {
  color: var(--cv-brand);
  text-decoration: none;
  font-weight: 500;
}

.cv-link:hover {
  text-decoration: underline;
}
</style>
